import { useState } from 'react';
import { Mail, Phone, MapPin, ExternalLink, X, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';

const QUICK_LINKS = [
  { label: 'Privacy Policy', path: '/privacy-policy' },
  { label: 'Terms & Conditions', path: '/terms-conditions' },
  { label: 'Contact Us', path: '/contact' },
  { label: 'About Us', path: '/about' },
  { label: 'Our Team', path: '/our-team' },
  { label: 'Safety Guidelines', path: '/safety' },
  { label: 'Help Center', path: '/help' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'How It Works', path: '/how-it-works' },
];

const SOCIALS = [
  { label: 'Facebook', icon: '/images/facebook.svg', url: 'https://www.facebook.com/totowalaindia' },
  { label: 'Instagram', icon: '/images/insta.svg', url: 'https://www.instagram.com/totowalaindia/' },
  { label: 'Twitter (X)', icon: '/images/twitter.svg', url: 'https://x.com/totowalaindia' },
  { label: 'LinkedIn', icon: '/images/linkedin.svg', url: 'https://linkedin.com/company/totowala' },
];

const APPS = [
  { id: 'driver', title: 'Driver App', image: '/images/driver_qr.jpg', link: 'https://play.google.com/store/apps/details?id=com.elt.totowalafordrivers' },
  { id: 'user', title: 'User App', image: '/images/user_qr.jpg', link: 'https://play.google.com/store/apps/details?id=com.elt.totowala' },
];

const Footer = () => {
  const [activePopup, setActivePopup] = useState(null);
  const activeApp = APPS.find((app) => app.id === activePopup);

  return (
    <footer className="bg-ink text-white">
      <div className="max-w-[1200px] mx-auto px-4 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Brand & downloads */}
          <div>
            <img src="/images/totowala-logo.svg" alt="TotoWala Logo" className="h-7 w-auto mb-5 brightness-0 invert" />
            <p className="font-display text-xl font-semibold text-white mb-2">Aapki Apni Sawari</p>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              Driver-first rides for everyone. Started in Bihar, expanding across India.
            </p>

            <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-3">Download Now</p>
            <a
              href="https://play.google.com/store/apps/details?id=com.elt.totowala"
              target="_blank" rel="noopener noreferrer"
              className="inline-block mb-6"
            >
              <img src="/images/download_google_play.webp" alt="Get it on Google Play" className="h-11 w-auto rounded-control" />
            </a>

            <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-3">Quick Download</p>
            <div className="flex gap-4">
              {APPS.map((app) => (
                <button key={app.id} onClick={() => setActivePopup(app.id)} className="text-center">
                  <img src={app.image} alt={`${app.title} QR Code`} className="w-16 h-16 rounded-control bg-white p-1" />
                  <p className="text-xs text-white/70 mt-2">{app.title}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {QUICK_LINKS.map(({ label, path }) => (
                <li key={path}>
                  <Link to={path} onClick={() => window.scrollTo(0, 0)} className="text-sm text-white/70 hover:text-white transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="pt-5 mt-5 border-t border-white/10">
              <p className="text-xs text-white/60">Started in Bihar · Expanding across India</p>
            </div>
          </div>

          {/* Contact & social */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-4">Get in Touch</h3>
            <div className="space-y-3 mb-7">
              <a href="mailto:contact@totowala.in" className="flex items-center gap-3 text-sm text-white/70 hover:text-white transition-colors">
                <Mail size={14} className="shrink-0 text-white/60" />
                contact@totowala.in
              </a>
              <a href="tel:+918088257606" className="flex items-center gap-3 text-sm text-white/70 hover:text-white transition-colors">
                <Phone size={14} className="shrink-0 text-white/60" />
                +91-8088257606
              </a>
              <div className="flex items-start gap-3 text-sm text-white/70">
                <MapPin size={14} className="shrink-0 text-white/60 mt-0.5" />
                <p className="leading-relaxed">
                  BE-41-139, Mayaganj - Saint Nagar Road,
                  Beside R.E.O Office Gate, Bhagalpur - 812003, Bihar, India
                </p>
              </div>
            </div>

            <h3 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-4">Follow Us</h3>
            <div className="flex gap-2.5 mb-4">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.url} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-control bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  aria-label={s.label}
                >
                  <img src={s.icon} alt="" className="w-4 h-4" />
                </a>
              ))}
            </div>
            <a href="/blog" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
              Visit our Blog
              <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1200px] mx-auto px-4 py-5 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-white/60">© 2026 TotoWala. All rights reserved.</p>
          <span className="flex items-center gap-1.5 text-xs text-white/60">
            Made with <Heart size={11} className="text-accent" fill="currentColor" /> in Bhagalpur
          </span>
        </div>
      </div>

      {/* QR Popup */}
      {activePopup && (
        <div
          className="fixed inset-0 bg-ink/70 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setActivePopup(null)}
        >
          <div className="bg-white rounded-card p-6 max-w-sm w-full relative">
            <button
              onClick={() => setActivePopup(null)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-surface-alt flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X size={18} className="text-ink-soft" />
            </button>

            <div className="text-center">
              <h3 className="text-lg font-bold text-ink mb-4">{activeApp?.title}</h3>
              <img
                src={activeApp?.image}
                alt={`${activeApp?.title} QR Code`}
                className="w-56 h-56 mx-auto rounded-control border border-line mb-5"
              />
              <p className="text-sm text-ink-soft mb-5">Scan this QR code or use the link below</p>
              <a
                href={activeApp?.link}
                target="_blank" rel="noopener noreferrer"
                onClick={() => setActivePopup(null)}
                className="block w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 rounded-control transition-colors"
              >
                Open in Play Store
              </a>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
